import { useSortable } from '@dnd-kit/sortable';
import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox } from '@mui/material';
import { createContext, useContext, useMemo } from 'react';
import {
  CheckboxContainer,
  StyledRequestItem,
  RequestTitle,
} from '../shared-drawer.styles';
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core';

export interface SortableRequestItemProps {
  id: UniqueIdentifier | string;
  title?: string;
  onSelect?: (id: UniqueIdentifier | string) => void;
  selected?: boolean;
}

interface Context {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ref() {},
});

export const SortableRequestItem = ({
  title = '',
  id,
  onSelect,
  selected = false,
}: SortableRequestItemProps) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  return (
    <SortableItemContext.Provider value={context}>
      <StyledRequestItem
        ref={setNodeRef}
        style={style}
        data-dragging={isDragging}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CheckboxContainer onClick={() => onSelect?.(id)}>
            <Checkbox color="secondary" checked={selected} />
          </CheckboxContainer>
          <RequestTitle>{title}</RequestTitle>
        </Box>
        <DragHandle />
      </StyledRequestItem>
    </SortableItemContext.Provider>
  );
};

export const DragHandle = () => {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <div
      {...attributes}
      {...listeners}
      ref={ref}
      style={{
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        touchAction: 'none',
      }}
    >
      <FontAwesomeIcon icon={faBars} size="xl" />
    </div>
  );
};
